// frontend/src/samkalp-admin/tabs/ContentBlocksTab.js
//
// Tenant-admin gate for Studio-generated SLM content (GeneratedContentBlock):
// pick a project, see its content blocks and current status, finalize
// (or unfinalize) each one — only FINALIZED blocks are visible to students
// in the content reader.
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Form, Button, Alert, Badge } from 'react-bootstrap';
import api, { getTenantProjects } from '../../api/api';
import { useAlert } from './shared';

const ContentBlocksTab = ({ slug }) => {
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [blocks, setBlocks] = useState([]);
    const [alert, showAlert] = useAlert();

    useEffect(() => {
        getTenantProjects(slug).then(res => setProjects(res.data || [])).catch(() => setProjects([]));
    }, [slug]);

    const loadBlocks = useCallback(() => {
        if (!projectId) { setBlocks([]); return; }
        api.get(`/tenant/${slug}/admin/projects/${projectId}/content-blocks`).then(res => setBlocks(res.data)).catch(() => setBlocks([]));
    }, [slug, projectId]);

    useEffect(() => { loadBlocks(); }, [loadBlocks]);

    const toggleFinalize = async (block) => {
        const action = block.status === 'finalized' ? 'unfinalize' : 'finalize';
        try {
            await api.patch(`/tenant/${slug}/admin/content-blocks/${block.id}/${action}`);
            showAlert(action === 'finalize' ? 'Marked finalized — visible to students now.' : 'Reverted to draft — hidden from students.');
            loadBlocks();
        } catch {
            showAlert('Could not update status.', 'danger');
        }
    };

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small">{alert.msg}</Alert>}

            <Card className="border-0 shadow-sm">
                <Card.Body>
                    <h6 className="fw-bold mb-3">Content Blocks — Finalize for Student Reader</h6>
                    <Form.Select size="sm" className="mb-3" style={{ maxWidth: 360 }} value={projectId} onChange={e => setProjectId(e.target.value)}>
                        <option value="">Select a project...</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </Form.Select>

                    {projectId && (
                        <Table hover responsive size="sm" className="align-middle">
                            <thead className="table-light">
                                <tr><th>Unit</th><th>Word Count</th><th>Preview</th><th>Status</th><th className="text-end">Action</th></tr>
                            </thead>
                            <tbody>
                                {blocks.map(b => (
                                    <tr key={b.id}>
                                        <td className="x-small">Unit {b.unit_id}</td>
                                        <td className="x-small">{b.word_count || '—'}</td>
                                        <td className="x-small text-muted" style={{ maxWidth: 320 }}>{b.summary_preview}</td>
                                        <td><Badge bg={b.status === 'finalized' ? 'success' : 'secondary'}>{b.status}</Badge></td>
                                        <td className="text-end">
                                            <Button size="sm" variant={b.status === 'finalized' ? 'outline-secondary' : 'primary'} onClick={() => toggleFinalize(b)}>
                                                {b.status === 'finalized' ? 'Unfinalize' : 'Finalize'}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {blocks.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-3">No content blocks for this project yet.</td></tr>}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default ContentBlocksTab;
